/**
 * M1 — "A Day in the Life" swipe deck.
 *
 * Verbatim from the original prototype `M1_TILES` (app.js L274-283), the
 * authoritative data source for icon + persona. 8 cards, order is meaningful
 * (alternates Alex/Sam). Persona is HIDDEN from the user — it only drives the
 * advisory M1 results lean (NOT the archetype; M2 is the branch point).
 *
 *   • `alex` = FP&A / corporate side
 *   • `sam`  = equity research / investing side
 *
 * Swipe right = "YES / I'd want this day"; left = "PASS"; a Skip link advances
 * without a YES for either side. Only right-swipes count toward the lean.
 *
 * Source of truth: docs/EXPERIENCE-MODEL.md §2. (Note: app.js tile 4 icon is the
 * authoritative `🪧` — content.js shows a corrupted glyph there.)
 */

export type M1Persona = 'alex' | 'sam';

export interface M1Card {
  icon: string;
  /** Position in the deck (0-indexed; order is meaningful). */
  idx: number;
  persona: M1Persona;
  text: string;
}

export const M1_CARDS: readonly M1Card[] = [
  { idx: 0, icon: '📊', text: "Check how last month's spending compared to the plan", persona: 'alex' },
  { idx: 1, icon: '📑', text: "Read Spotify's latest financial results", persona: 'sam' },
  { idx: 2, icon: '📞', text: 'Meet with other teams to plan the budget', persona: 'alex' },
  { idx: 3, icon: '📈', text: 'Update the cash flow model on Nvidia', persona: 'sam' },
  { idx: 4, icon: '🪧', text: 'Build slide deck for the CFO', persona: 'alex' },
  { idx: 5, icon: '🎙️', text: 'Listen to a live earnings call', persona: 'sam' },
  { idx: 6, icon: '💵', text: "Forecast next quarter's costs", persona: 'alex' },
  { idx: 7, icon: '✉️', text: 'Pitch a stock to the Portfolio Manager', persona: 'sam' },
];

/** Labels for the two-color persona bar on the M1 results card. */
export const M1_PERSONA_LABELS: Record<M1Persona, string> = {
  alex: 'FP&A',
  sam: 'Investing',
};

/**
 * M1 results copy — the ADVISORY summary of the swipe lean (NOT the archetype branch).
 * Verbatim from the original prototype `CONTENT.m1a.results` (content.js L94-117) and the
 * `drawM1Results` bucket logic (app.js L946-959). Source of truth: docs/EXPERIENCE-MODEL.md §2.
 *
 * The `fpaPct` (share of "yes" right-swipes that landed on `alex`/FP&A cards) selects one of five
 * interpretation buckets. M1 is advisory only — nothing downstream is gated on this lean.
 */
export const M1_RESULTS_COPY = {
  /** Bridge line shown under the interpretation; renders `think` emphasized. */
  bridge: {
    pre: 'Next: 4 questions about how you ',
    emphasis: 'think',
    post: " about money decisions. That's the other half of your archetype.",
  },
  interpretation: {
    strongFpa:
      "A clear lean toward FP&A. You're drawn to the inside view: cost discipline, margin levers, building the engine that makes a business work.",
    strongInvest:
      "A clear lean toward investing. You're drawn to the outside view: pricing, mispricing, and the discipline of taking a position with conviction.",
    moderateFpa:
      "A modest lean toward FP&A. You're drawn to the inside view, but the public-markets side still pulled you on some days.",
    moderateInvest:
      "A modest lean toward investing. The markets-facing work pulled you, but you stayed open to the builder's craft too.",
    split:
      "You're genuinely split. Both worlds pulled you on different days. That's a signal, not indecision. The next module will sharpen which side fits how you actually think.",
  },
} as const;

export type M1InterpretationKey = keyof typeof M1_RESULTS_COPY.interpretation;

export interface M1Lean {
  /** Whole-percent FP&A share of yeses (50 when there are no yeses). */
  fpaPct: number;
  /** The interpretation copy for `interpretationKey`. */
  interpretation: string;
  /** The selected interpretation bucket key. */
  interpretationKey: M1InterpretationKey;
  /** Whole-percent Investing share (= 100 − fpaPct, modulo rounding). */
  samPct: number;
  /** Right-swipe ("yes") count on FP&A (alex) cards. */
  yesAlex: number;
  /** Right-swipe ("yes") count on Investing (sam) cards. */
  yesSam: number;
}

/**
 * Compute the advisory M1 lean from the store's swipe answers — the EXACT logic from the original
 * `drawM1Results` (app.js L927-959). Counts only RIGHT swipes ("yes") per persona; left/skip don't
 * count toward either side. `fpaPct` anchors the 5-bucket interpretation:
 *   ≥65% strongFpa · ≤35% strongInvest · >55% moderateFpa · <45% moderateInvest · else split.
 *
 * `m1Answers` is the store map `Record<cardId, 'left'|'right'>` (cardId = `String(card.idx)`).
 */
export function computeM1Lean(m1Answers: Record<string, 'left' | 'right'>): M1Lean {
  let yesAlex = 0;
  let yesSam = 0;
  for (const card of M1_CARDS) {
    if (m1Answers[String(card.idx)] === 'right') {
      if (card.persona === 'alex') {
        yesAlex += 1;
      } else {
        yesSam += 1;
      }
    }
  }
  const total = yesAlex + yesSam;
  const fpaPctRaw = total > 0 ? (yesAlex / total) * 100 : 50;
  const fpaPct = Math.round(fpaPctRaw);
  const samPct = 100 - fpaPct;

  let interpretationKey: M1InterpretationKey;
  if (fpaPctRaw >= 65) {
    interpretationKey = 'strongFpa';
  } else if (fpaPctRaw <= 35) {
    interpretationKey = 'strongInvest';
  } else if (fpaPctRaw > 55) {
    interpretationKey = 'moderateFpa';
  } else if (fpaPctRaw < 45) {
    interpretationKey = 'moderateInvest';
  } else {
    interpretationKey = 'split';
  }

  return {
    yesAlex,
    yesSam,
    fpaPct,
    samPct,
    interpretationKey,
    interpretation: M1_RESULTS_COPY.interpretation[interpretationKey],
  };
}
